// Typed client for /api/v1/process-studio — mirrors services/projectApi.ts's
// style (thin axios wrappers over the shared `api` instance, one call per
// route). Split out of ProcessStudioPage.tsx so the request/response shapes
// have one place to live as the folder-tree + move surface grows.
//
// BP 26-03 P1 (migration 155) — process_studio_diagrams gained project_id/
// ordinal/user_id/visibility/public_id/kind, plus GET ?project_id=|
// project_ids=|__inbox (folder-scoped listing, same contract as guides) and
// POST /:id/move (re-park). See backend/src/controllers/processStudioController.js
// for the source of truth this file mirrors — read that before changing the
// shapes here.

import { api } from './apiClient';

/**
 * The `images` block a create/content-replace response carries when the
 * import found local `<img>`/`srcset`/`poster`/`url()` refs in the HTML (BP
 * 63 image picker). `referenced` is how many distinct local refs the server
 * found; `inlined` is how many it successfully embedded as data URIs;
 * `skipped_data` counts refs that were already data: URIs (nothing to do);
 * `unresolved` lists every ref it could NOT inline, with why. See the
 * process-studio controller's upload branch for the source of truth.
 */
export interface ImportImagesReport {
  referenced: number;
  inlined: number;
  skipped_data: number;
  unresolved: { src: string; reason: 'missing' | 'ambiguous' | 'too_large' | 'budget' | 'decode_failed' }[];
  bytes_before: number;
  bytes_after: number;
}

export interface DiagramMeta {
  id: string;
  title: string | null;
  lane_count: number | null;
  source_files: { name: string }[];
  imported?: boolean;          // a pre-built HTML5 import (served as-designed)
  /** Folder this diagram is parked under; null/absent = unfiled ("Inbox"). */
  project_id?: string | null;
  ordinal?: number;
  visibility?: 'private' | 'org' | 'public';
  user_id?: string | null;
  public_id?: string;
  /** 'flow' (a PageSnip Flow — a process diagram; swimlane and phase are two
   *  VIEWS of the same model, not separate kinds) | 'guide' (a PageSnip
   *  Guide — a chapter-based document with its own left nav). The API is
   *  mid-migration off the old values 'flowsnip'→'flow' and 'pagesnip'→
   *  'guide'; a row can still arrive with a legacy value, or none at all
   *  (pre-155 backfill). Never compare `kind` directly — always go through
   *  `normalizeKind()` below, the one place that reconciles old/new/missing. */
  kind?: 'flow' | 'guide' | 'flowsnip' | 'pagesnip' | string;
  created_at: string;
  updated_at: string;
  /** Lifetime total of external+owner comments on this diagram (list's
   *  grouped-COUNT, computed once for the whole page — see
   *  processStudioController.list()'s header for why this isn't per-row). */
  comment_count?: number;
  /** Most recent comment's created_at, or null/absent when there are none.
   *  The list never reports "unseen" itself — the UI compares this against a
   *  per-diagram last-opened timestamp it keeps client-side (localStorage). */
  last_comment_at?: string | null;
  /** PRIVATE to the caller (migration 163) — never a shared property of the
   *  diagram itself. Computed once for the whole page (list()'s grouped
   *  PageSnipBookmark query, same N+1-avoidance shape as comment_count
   *  above), never per-row. This flat flag IS "list the caller's bookmarks"
   *  — the toolbar's bookmarks-only toggle filters this same array, exactly
   *  like the existing unfiled toggle filters on `project_id`. */
  bookmarked?: boolean;
}

/** Single source of truth for reading `kind` during the flowsnip→flow /
 *  pagesnip→guide rollout: accepts the new values, the legacy values, or
 *  missing/unknown, and always returns a current-taxonomy value. Missing/
 *  unknown falls back to 'flow' (the historical default — matches the
 *  pre-155 backfill and what an ungated `create()` without an explicit
 *  `kind` has always produced). Every kind check in the UI should go
 *  through this rather than a scattered `=== 'flowsnip'` ternary. */
export function normalizeKind(kind?: string | null): 'flow' | 'guide' {
  if (kind === 'guide' || kind === 'pagesnip') return 'guide';
  return 'flow';
}

export interface ListParams {
  /** A single folder id, or '__inbox' for unfiled diagrams. Omit for "all". */
  projectId?: string | '__inbox' | null;
  /** A folder + its descendant ids (client-computed, like guides' "include
   *  subfolders"). Takes precedence over `projectId` when both are set. */
  projectIds?: string[];
}

export const flowsnipApi = {
  /** GET /api/v1/process-studio — folder-scoped list. Same query contract as
   *  GET /api/v1/guides: project_id=<id> | project_id=__inbox |
   *  project_ids=a,b,c | (no param) = everything the caller can see. */
  async list(params?: ListParams): Promise<DiagramMeta[]> {
    const q = new URLSearchParams();
    if (params?.projectIds?.length) {
      q.set('project_ids', params.projectIds.join(','));
    } else if (params?.projectId) {
      q.set('project_id', params.projectId);
    }
    const qs = q.toString();
    const r = await api.get(`/api/v1/process-studio${qs ? `?${qs}` : ''}`);
    return (r.data?.diagrams ?? []) as DiagramMeta[];
  },

  /**
   * POST /api/v1/process-studio — create (import a self-contained .html/.htm,
   * or generate a swimlane model from multi-file/pasted source). `form` is
   * built by the create modal (files/content/title/laneCount/model, plus an
   * optional `kind` field — 'flow' | 'guide' — only meaningful for the
   * single-HTML import branch; the backend auto-detects when it's omitted.
   * See processStudioController.create's HTML5-import branch / detectKind).
   */
  async create(form: FormData): Promise<{ diagram: DiagramMeta; warnings?: string[]; changes?: string[]; images?: ImportImagesReport }> {
    const r = await api.post('/api/v1/process-studio', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      diagram: r.data?.diagram as DiagramMeta,
      warnings: r.data?.warnings as string[] | undefined,
      changes: r.data?.changes as string[] | undefined,
      images: r.data?.images as ImportImagesReport | undefined,
    };
  },

  /**
   * POST /api/v1/process-studio/build — author a PageSnip with AI from a
   * subject/topic (`subject`, required) plus optional `content` (source
   * material/notes), `files` (0..N reference files), `model`, and `title`
   * override. Distinct from `create()`'s file-import/generate-from-source
   * flow — this is "start from a topic, not a document". Building runs an AI
   * call (20-60s), so callers should show a pending state. Response shape
   * mirrors create's 201 but wraps the diagram alongside any non-fatal
   * `warnings` (see processStudioController's /build route for the 422
   * error codes: missing_subject, pagesnip_external_ref, pagesnip_bad_json,
   * pagesnip_forbidden_tag, pagesnip_toc_mismatch).
   */
  async build(form: FormData): Promise<{ diagram: DiagramMeta; warnings?: string[]; images?: ImportImagesReport }> {
    const r = await api.post('/api/v1/process-studio/build', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      diagram: r.data?.diagram as DiagramMeta,
      warnings: r.data?.warnings as string[] | undefined,
      images: r.data?.images as ImportImagesReport | undefined,
    };
  },

  /**
   * PATCH /api/v1/process-studio/:id — rename. `title` is server-sliced to
   * 200 chars (see processStudioController.update); caller is expected to
   * trim/no-op an unchanged title itself, same as the folder tree's own
   * inline rename does before calling projectApi.rename/guideTreeApi.rename.
   */
  async rename(id: string, title: string): Promise<DiagramMeta> {
    const r = await api.patch(`/api/v1/process-studio/${id}`, { title });
    return r.data?.diagram as DiagramMeta;
  },

  /**
   * POST /api/v1/process-studio/:id/move — re-park a diagram into `projectId`
   * (null = Inbox/unfiled).
   *
   * `ordinal` is accepted for interface parity with the Projects Tree's
   * guide/project move calls, but is NOT currently wired to a request field:
   * the backend's /move only reorders a folder's members via a full
   * `ordered_ids` array for that destination folder (see
   * processStudioController.move's header comment), which a simple
   * drag-a-card-onto-a-folder-row gesture doesn't have on hand — and doesn't
   * need to, since the diagram list always sorts by `updated_at DESC`
   * server-side (list()'s `order`), never by ordinal. This call only re-parks.
   */
  async move(id: string, projectId: string | null, ordinal?: number): Promise<DiagramMeta> {
    void ordinal;   // see note above
    const r = await api.post(`/api/v1/process-studio/${id}/move`, { project_id: projectId });
    return r.data?.diagram as DiagramMeta;
  },

  /**
   * POST /api/v1/process-studio/:id/content — replace the stored HTML IN
   * PLACE (multipart, field `file`, a single .html/.htm). The item keeps its
   * id, share link, grants, threads, folder, title, and position — only the
   * rendered content changes. This is the "revise v1→v4" path: today that
   * means delete-and-reimport, which destroys the share link and threads;
   * this call is the fix. Response mirrors `create`'s diagram plus any
   * non-fatal `changes`/`warnings` describing what the import noticed (same
   * shape family as `build`'s warnings) — see ItemActionsMenu's "Replace
   * content" action for how those are surfaced.
   */
  async replaceContent(
    id: string,
    file: File,
    images: File[] = [],
    imageMap?: Record<string, string>,
  ): Promise<{ diagram: DiagramMeta; changes?: string[]; warnings?: string[]; images?: ImportImagesReport }> {
    const form = new FormData();
    form.append('file', file);
    for (const img of images) form.append('images', img, img.name);
    if (imageMap) form.append('image_map', JSON.stringify(imageMap));
    const r = await api.post(`/api/v1/process-studio/${id}/content`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      diagram: r.data?.diagram as DiagramMeta,
      changes: r.data?.changes as string[] | undefined,
      warnings: r.data?.warnings as string[] | undefined,
      images: r.data?.images as ImportImagesReport | undefined,
    };
  },

  /**
   * POST /api/v1/process-studio/:id/duplicate — a fresh, private copy titled
   * "… (copy)". Deliberately does NOT carry over the share link, grants, or
   * threads — those are specific to the original item's identity.
   */
  async duplicate(id: string): Promise<DiagramMeta> {
    const r = await api.post(`/api/v1/process-studio/${id}/duplicate`);
    return r.data?.diagram as DiagramMeta;
  },

  /**
   * POST /api/v1/process-studio/:id/bookmark — toggle the CALLER's private
   * "read later" flag on this diagram (migration 163). Flips by existence
   * server-side (find-then-create/destroy against a UNIQUE (user_id,
   * diagram_id) index) — this call carries no body, the response says which
   * way it landed. `{ bookmarked }` is the response ROOT, no nesting.
   */
  async toggleBookmark(id: string): Promise<boolean> {
    const r = await api.post(`/api/v1/process-studio/${id}/bookmark`);
    return !!r.data?.bookmarked;
  },

  /**
   * PATCH /api/v1/process-studio/:id — visibility only. Thin wrapper over
   * the same endpoint `rename()` uses, kept as its own call so a caller
   * changing just the audience doesn't have to build the partial-update
   * body itself.
   */
  async setVisibility(id: string, visibility: 'private' | 'org' | 'public'): Promise<DiagramMeta> {
    const r = await api.patch(`/api/v1/process-studio/${id}`, { visibility });
    return r.data?.diagram as DiagramMeta;
  },

  // — People & access panel (BP 26-03 P1 grants surface). Routes already
  // exist server-side (processStudio.js) but no UI consumes these yet —
  // typed now so that follow-up is a small, self-contained addition.
  async grants(id: string): Promise<unknown> {
    const r = await api.get(`/api/v1/process-studio/${id}/grants`);
    return r.data;
  },

  async inheritedAccess(id: string): Promise<unknown> {
    const r = await api.get(`/api/v1/process-studio/${id}/inherited-access`);
    return r.data;
  },
};
